if (!customElements.get('shipping-calculator')) {
  const SHIPPING_ZIP_STORAGE_KEY = 'homedock_shipping_zip';

  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.button.addEventListener('click', this.handleSubmit.bind(this));

      const savedZip = this.getSavedZip();
      const zipInput = this.querySelector('.js-zip-input');
      if (savedZip && zipInput) {
        zipInput.value = savedZip;

        if (this.closest('cart-drawer')) {
          window.setTimeout(() => {
            if (this.isConnected && !this.button.disabled) this.handleSubmit();
          }, 250);
        }
      }
    }

    init() {
      this.country = this.querySelector('.js-country-select');
      this.province = this.querySelector('.js-province-select');
      this.zip = this.querySelector('.js-zip-input');
      this.rates = this.querySelector('.js-rates');
      this.errors = this.querySelector('.js-errors');
      this.initialised = true;
    }

    /**
     * Handles submission of the rates calculator.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt?.preventDefault();
      if (this.button.disabled) return;
      if (!this.initialised) this.init();

      const zipValue = this.zip.value.trim();
      if (zipValue) this.saveZip(zipValue);

      this.errors.hidden = true;
      this.rates.hidden = true;
      this.button.disabled = true;
      this.button.querySelector('.loading__spinner').classList.remove('hidden');
      this.button.querySelector('.button-text').classList.add('hidden');

      const params = `shipping_address[zip]=${this.zip.value}&shipping_address[country]=${this.country.value}&shipping_address[province]=${this.province.value}`;

      try {
        const response = await fetch(`${theme.routes.cart}/shipping_rates.json?${params}`);
        const data = await response.json();

        if (response.ok) {
          this.showRates(data);
        } else {
          this.showErrors(data);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        this.button.querySelector('.loading__spinner').classList.add('hidden');
        this.button.querySelector('.button-text').classList.remove('hidden');
        this.button.disabled = false;
      }
    }

    getSavedZip() {
      try {
        return window.localStorage.getItem(SHIPPING_ZIP_STORAGE_KEY) || '';
      } catch (error) {
        return '';
      }
    }

    saveZip(zip) {
      try {
        window.localStorage.setItem(SHIPPING_ZIP_STORAGE_KEY, zip);
      } catch (error) {
        // Storage can be unavailable in private browsing; calculation still works.
      }
    }

    restoreSavedQuote() {
      const savedZip = this.getSavedZip();
      const zipInput = this.querySelector('.js-zip-input');
      if (!savedZip || !zipInput) return;

      zipInput.value = savedZip;
      window.setTimeout(() => {
        if (this.isConnected && !this.button.disabled) this.handleSubmit();
      }, 350);
    }

    /**
     * Formats and shows a list of available shipping rates.
     * @param {object} data - Response data.
     */
    showRates(data) {
      const headingEl = this.rates.querySelector('.js-rates-heading');
      const textEl = this.rates.querySelector('.js-rates-text');
      textEl.innerHTML = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        const headingLocaleStr = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        let rates = '';

        data.shipping_rates.forEach((rate) => {
          const isFreeShipping = (Number.parseFloat(rate.price) || 0) === 0;
          let formattedRate;
          if (rate.price > 0) {
            formattedRate = rate.currency === 'BRL' 
              ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rate.price) 
              : theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, rate.price);
          } else {
            formattedRate = 'Frete Grátis'
          }

          const deliveryDate = this.getDeliveryDate(rate);
          const deliveryText = deliveryDate
            ? `<span class="shipping-rate-delivery"><span>Entrega até dia:</span><b>${deliveryDate}</b></span>`
            : '';
          rates += `<li><p><span class="shipping-rate-option"><span>${this.escapeHTML(rate.name)}:</span><b${isFreeShipping ? ' class="shipping-rate-free"' : ''}>${formattedRate}</b></span>${deliveryText}</p></li>`;
        });

        this.updateCartSummary(data.shipping_rates[0]);

        headingEl.hidden = false;
        headingEl.textContent = theme.strings.shippingCalculator[headingLocaleStr];
        textEl.innerHTML = `<ul class="styled-list">${rates}</ul>`;
      } else {
        headingEl.hidden = true;
        textEl.innerHTML = theme.strings.shippingCalculator.noRates;
        this.updateCartSummary(null);
      }

      this.rates.hidden = false;
    }

    /**
     * Adds the quoted shipping rate to the visual total in the cart drawer.
     * Shopify recalculates the definitive total again during checkout.
     * @param {object|null} rate - The primary rate returned by Shopify.
     */
    updateCartSummary(rate) {
      const drawer = this.closest('cart-drawer');
      const cartPage = this.closest('#main-cart-footer');
      const summary = drawer
        ? drawer.querySelector('[data-cart-summary]')
        : cartPage?.querySelector('[data-cart-summary]');
      if (!summary) return;

      const shippingEl = summary.querySelector('[data-cart-shipping-value]');
      const deliveryRow = summary.querySelector('[data-cart-delivery-row]');
      const deliveryDateEl = summary.querySelector('[data-cart-delivery-date]');
      const totalEl = summary.querySelector('[data-cart-total-value]');
      const subtotalCents = Number.parseInt(summary.dataset.cartSubtotalCents, 10) || 0;
      const cardSubtotalCents = Number.parseInt(summary.dataset.cartCardSubtotalCents, 10) || subtotalCents;

      if (!rate) {
        shippingEl.textContent = 'A calcular';
        shippingEl.classList.remove('shipping-rate-free');
        if (deliveryRow) deliveryRow.hidden = true;
        totalEl.textContent = this.formatBRL(subtotalCents);
        this.updateInstallmentSummary(cardSubtotalCents);
        return;
      }

      const shippingCents = Math.round((Number.parseFloat(rate.price) || 0) * 100);
      shippingEl.textContent = shippingCents > 0 ? this.formatBRL(shippingCents) : 'Grátis';
      shippingEl.classList.toggle('shipping-rate-free', shippingCents === 0);

      const deliveryDate = this.getDeliveryDate(rate);
      if (deliveryRow && deliveryDateEl) {
        deliveryDateEl.textContent = deliveryDate;
        deliveryRow.hidden = !deliveryDate;
      }

      const totalCents = subtotalCents + shippingCents;
      totalEl.textContent = this.formatBRL(totalCents);
      this.updateInstallmentSummary(cardSubtotalCents + shippingCents);
    }

    /**
     * Recalculates the card total and interest-free installments with shipping.
     * @param {number} totalCents - Products plus quoted shipping, in cents.
     */
    updateInstallmentSummary(totalCents) {
      const drawer = this.closest('cart-drawer');
      const cartPage = this.closest('#main-cart-footer');
      const installment = drawer
        ? drawer.querySelector('[data-cart-installment]')
        : cartPage?.querySelector('[data-cart-installment]');
      if (!installment) return;

      const content = installment.querySelector('[data-cart-installment-content]');
      const cardTotal = installment.querySelector('[data-cart-card-total]');
      const count = installment.querySelector('[data-cart-installment-count]');
      const value = installment.querySelector('[data-cart-installment-value]');
      const minPrice = Number.parseFloat(
        String(installment.dataset.minInstallmentPrice || '').replace(/\./g, '').replace(',', '.'),
      );
      const maxInstallments = Number.parseInt(installment.dataset.installmentsMax, 10);

      if (!content || !cardTotal || !count || !value || !minPrice || !maxInstallments) return;

      const installments = Math.max(1, Math.min(maxInstallments, Math.floor(totalCents / 100 / minPrice)));
      content.hidden = installments <= 1;
      cardTotal.textContent = this.formatBRL(totalCents);
      count.textContent = installments;
      value.textContent = this.formatBRL(Math.floor(totalCents / installments));
    }

    /**
     * Formats a value stored in cents as Brazilian currency.
     * @param {number} cents - Monetary value in cents.
     * @returns {string}
     */
    formatBRL(cents) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(cents / 100);
    }

    /**
     * Returns a formatted delivery date based on the shipping rate business days.
     * @param {object} rate - Shipping rate from Shopify.
     * @returns {string}
     */
    getDeliveryDate(rate) {
      const deliveryDays = Array.isArray(rate.delivery_days) ? parseInt(rate.delivery_days[0], 10) : null;
      if (!deliveryDays || deliveryDays < 1) return '';

      const date = new Date();
      let remainingDays = deliveryDays;

      while (remainingDays > 0) {
        date.setDate(date.getDate() + 1);

        const day = date.getDay();
        if (day !== 0 && day !== 6) {
          remainingDays -= 1;
        }
      }

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      }).format(date);
    }

    /**
     * Escapes text before injecting shipping rate names into HTML.
     * @param {string} text - Raw text.
     * @returns {string}
     */
    escapeHTML(text) {
      return String(text).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[char]));
    }

    /**
     * Formats and shows errors relating to rates retrieval.
     * @param {object} data - Response data.
     */
    showErrors(data) {
      let errors = '';

      Object.keys(data).forEach((key) => {
        errors += `<li>${data[key]}</li>`;
      });

      this.errors.querySelector('.js-errors-text').innerHTML = `<ul class="styled-list">${errors}</ul>`;
      this.errors.hidden = false;
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);

  const zipInputSelector = 'input[autocomplete="postal-code"], input[name*="cep" i], input[id*="cep" i], input[placeholder*="cep" i], input[name*="postal" i], input[id*="postal" i], .js-zip-input';

  const savePotentialZip = (value) => {
    const zip = String(value || '').trim();
    if (zip.replace(/\D/g, '').length !== 8) return false;

    try {
      window.localStorage.setItem(SHIPPING_ZIP_STORAGE_KEY, zip);
      return true;
    } catch (error) {
      return false;
    }
  };

  const findZipInInputs = (root = document) => {
    const inputs = Array.from(root.querySelectorAll('input'));

    for (const input of inputs) {
      const type = (input.type || 'text').toLowerCase();
      if (!['text', 'tel', 'number', 'search'].includes(type)) continue;
      if (savePotentialZip(input.value)) return true;
    }

    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot && findZipInInputs(element.shadowRoot)) return true;
    }

    return false;
  };

  const restoreSavedZip = (input) => {
    if (!input || input.value.trim()) return;

    try {
      const savedZip = window.localStorage.getItem(SHIPPING_ZIP_STORAGE_KEY);
      if (savedZip) input.value = savedZip;
    } catch (error) {
      // Ignore storage restrictions without interrupting the customer's flow.
    }
  };

  document.querySelectorAll(zipInputSelector).forEach(restoreSavedZip);

  document.addEventListener('focusin', (event) => {
    const zipInput = event.target.closest(zipInputSelector);
    if (zipInput) restoreSavedZip(zipInput);
  });

  document.addEventListener('input', (event) => {
    const zipInput = event.target.closest(zipInputSelector);
    if (zipInput) {
      savePotentialZip(zipInput.value);
      return;
    }

    if (event.target.matches('input')) savePotentialZip(event.target.value);
  });

  document.addEventListener('click', () => {
    findZipInInputs();  
  }, true);

  window.HomedockShipping = {
    restoreCartDrawer() {
      const calculator = document.querySelector('cart-drawer shipping-calculator');
      if (calculator && typeof calculator.restoreSavedQuote === 'function') {
        calculator.restoreSavedQuote();
      }
    },
    captureProductZip() {
      return findZipInInputs();
    },
  };

  document.addEventListener('cart:zip-restore', () => {
    window.HomedockShipping.restoreCartDrawer();
  });
}
